import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Target, CheckCircle, ListTodo, Trophy } from "lucide-react";
import { useLocation } from "wouter";
import type { DashboardStats } from "@shared/schema";

export default function StatsOverview() {
  const [, setLocation] = useLocation();

  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/stats/dashboard"],
  });

  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-16 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      title: "Goals Outstanding",
      value: Number(stats.activeGoals) || 0,
      icon: Target,
      color: "text-primary",
      bgColor: "bg-primary/10",
      change: `${Number(stats.completedGoals) || 0} completed`,
      positive: true,
      navigateTo: "/goals",
    },
    {
      title: "Tasks Outstanding",
      value: Number(stats.activeTasks) || 0,
      icon: ListTodo,
      color: "text-blue-600",
      bgColor: "bg-blue-600/10",
      change: `${Number(stats.completedTasks) || 0} completed`,
      positive: true,
      navigateTo: "/tasks",
    },
    {
      title: "Achievements",
      value: Number(stats.completedGoals || 0) + Number(stats.completedTasks || 0),
      icon: Trophy,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      change: `Success rate: ${Number(stats.successRate) || 0}%`,
      positive: true,
      navigateTo: "/goals",
    },
    {
      title: "Active Habits",
      value: Number(stats.activeHabits) || 0,
      icon: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-600/10",
      change: "Track daily habits",
      positive: true,
      navigateTo: "/habits",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {statCards.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card 
            key={stat.title} 
            className="hover:shadow-md transition-all cursor-pointer hover:scale-[1.02]"
            onClick={() => setLocation(stat.navigateTo)}
            data-testid={`card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm font-medium">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-foreground mt-1" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                {stat.positive ? (
                  <span className="text-success flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L10 4.414 4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {stat.change}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{stat.change}</span>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
